import { getSafeDefaultValue, updateSimulatorQueryParams } from '@/utils/simulators';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useForm } from "react-hook-form";
import _isEqualWith from 'lodash/isEqualWith';

const MIN_INITIAL_CAPITAL = 0;
const DEFAULT_INITIAL_CAPITAL = 10_000;

const MIN_MONTHLY_SAVINGS = 0;
const DEFAULT_MONTHLY_SAVINGS = 100;

const MIN_INVESTMENT_HORIZON = 1;
const DEFAULT_INVESTMENT_HORIZON = 20;

const MIN_INTEREST_RATE = 0;
const MAX_INTEREST_RATE = 100;
const DEFAULT_INTEREST_RATE = 5;

const MIN_COMPOUND_PERIODS_IN_MONTHS = 1;
const MAX_COMPOUND_PERIODS_IN_MONTHS = 12;
const DEFAULT_COMPOUND_PERIODS_IN_MONTHS = 12;

const DEFAULT_VALUES = {
  initial_capital: DEFAULT_INITIAL_CAPITAL,
  monthly_savings: DEFAULT_MONTHLY_SAVINGS,
  investment_horizon: DEFAULT_INVESTMENT_HORIZON,
  interest_rate: DEFAULT_INTEREST_RATE,
  interest_rate_every_x_months: DEFAULT_COMPOUND_PERIODS_IN_MONTHS,
}

export type CompoundInterestsFormValues = {
    initial_capital: number;
    monthly_savings: number;
    investment_horizon: number;
    interest_rate: number;
    interest_rate_every_x_months: number;
}

export const useCompoundInterestsSimulatorForm = () => {
  const initializedRef = React.useRef(false);
  const paramsInitializedRef = React.useRef(false);
    const form = useForm<CompoundInterestsFormValues>({
        mode: 'onChange',
        defaultValues: DEFAULT_VALUES,
    });

    const { query, isReady } = useRouter();

    const values = form.watch();

    React.useEffect(() => {
      if (isReady && !initializedRef.current) {
        initializedRef.current = true;
        form.reset({
          initial_capital: getSafeDefaultValue({
            value: query.initial_capital,
            defaultValue: DEFAULT_INITIAL_CAPITAL,
            min: MIN_INITIAL_CAPITAL,
          }),
          monthly_savings: getSafeDefaultValue({
            value: query.monthly_savings,
            defaultValue: DEFAULT_MONTHLY_SAVINGS,
            min: MIN_MONTHLY_SAVINGS,
          }),
          investment_horizon: getSafeDefaultValue({
            value: query.investment_horizon,
            defaultValue: DEFAULT_INVESTMENT_HORIZON,
            min: MIN_INVESTMENT_HORIZON,
          }),
          interest_rate: getSafeDefaultValue({
            value: query.interest_rate,
            defaultValue: DEFAULT_INTEREST_RATE,
            min: MIN_INTEREST_RATE,
            max: MAX_INTEREST_RATE,
          }),
          interest_rate_every_x_months: getSafeDefaultValue({
            value: query.interest_rate_every_x_months,
            defaultValue: DEFAULT_COMPOUND_PERIODS_IN_MONTHS,
            min: MIN_COMPOUND_PERIODS_IN_MONTHS,
            max: MAX_COMPOUND_PERIODS_IN_MONTHS,
          }),
        })
      } else if (!_isEqualWith(DEFAULT_VALUES, values) || paramsInitializedRef.current) {
        paramsInitializedRef.current = true;
        updateSimulatorQueryParams(values);
      }
    }, [isReady, query, form, values])

    const initialCapital = form.watch('initial_capital');
    const monthlySavings = form.watch('monthly_savings');
    const interestRate = form.watch('interest_rate') / 100;
    const compoundPeriodsInMonths = form.watch('interest_rate_every_x_months');
    const compoundPeriodsPerYear = 12 / compoundPeriodsInMonths;
    const numbersOfYears = (Number.isNaN(form.watch('investment_horizon')) || form.watch('investment_horizon') < 0) ? 0 : form.watch('investment_horizon');

    const isInvalid = Number.isNaN(initialCapital) || 
      Number.isNaN(monthlySavings) || 
      Number.isNaN(interestRate) || 
      Number.isNaN(compoundPeriodsInMonths) || 
      numbersOfYears < MIN_INVESTMENT_HORIZON ||
      compoundPeriodsInMonths < MIN_COMPOUND_PERIODS_IN_MONTHS || 
      compoundPeriodsInMonths > MAX_COMPOUND_PERIODS_IN_MONTHS ||
      interestRate < MIN_INTEREST_RATE ||
      interestRate > MAX_INTEREST_RATE ||
      monthlySavings < 0 ||
      initialCapital < MIN_INITIAL_CAPITAL;

    return {
        form,
        isInvalid,
        initialCapital,
        monthlySavings,
        interestRate,
        compoundPeriodsPerYear,
        numbersOfYears,
    }
}